import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { createCheckout } from "../lib/api";
import { toast } from "sonner";
import { trackBeginCheckout } from "../lib/analytics";
import { CartItem } from "./cart/CartItem";
import { FreeShippingBar } from "./cart/FreeShippingBar";
import { SetUpsellCard } from "./cart/SetUpsellCard";
import { CartTotals } from "./cart/CartTotals";

const _buildPartnerSuggestions = (sets, qtyById) =>
  sets
    .map((s) => {
      const inCart = s.product_ids.filter((pid) => (qtyById?.[pid] || 0) >= 1);
      if (inCart.length !== 1) return null;
      const missingId = s.product_ids.find((pid) => !inCart.includes(pid));
      const partner = (s.products || []).find((p) => p.id === missingId);
      if (!partner) return null;
      return { set: s, partner };
    })
    .filter(Boolean)
    .slice(0, 1);

export const CartDrawer = () => {
  const {
    items, isOpen, setIsOpen, updateQty, removeItem,
    subtotal, appliedSets, setSavings, total,
    sets, qtyById, addItem,
  } = useCart();
  const [loading, setLoading] = useState(false);

  const partnerSuggestions = _buildPartnerSuggestions(sets, qtyById);

  const onCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
        size: i.size,
        color: i.color,
      }));
      trackBeginCheckout({
        value: total,
        items: items.map((i) => ({
          item_id: i.product_id,
          item_name: i.name,
          quantity: i.quantity,
          price: i.price,
        })),
      });
      const res = await createCheckout(payload, window.location.origin);
      if (res.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onAddPartner = (partner, set) => {
    const partnerSize = partner.sizes?.[1] || partner.sizes?.[0];
    const partnerColor = partner.colors?.[0];
    addItem(partner, { size: partnerSize, color: partnerColor, quantity: 1 });
    toast.success(`${set.name} complete — saving £${set.discount.toFixed(2)}.`);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="bg-[#f0e9e1] border-l border-[#1a1814]/10 w-full sm:max-w-md p-0 text-[#1a1814]"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-8 pb-4 border-b border-[#1a1814]/10">
          <SheetTitle className="font-display text-2xl text-[#1a1814] text-left">Your Atelier Bag</SheetTitle>
          <SheetDescription className="micro-label text-[#1a1814]/60 text-left">
            {items.length} {items.length === 1 ? "piece" : "pieces"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)]">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {items.length === 0 ? (
              <div className="py-20 text-center" data-testid="cart-empty">
                <p className="font-display text-2xl text-[#1a1814] mb-2">Your bag is empty.</p>
                <p className="text-sm text-[#1a1814]/60">Pieces you save will appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#1a1814]/10">
                {items.map((item) => (
                  <CartItem
                    key={item.key}
                    item={item}
                    onUpdateQty={updateQty}
                    onRemove={removeItem}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-[#1a1814]/10 px-6 py-6 bg-[#efe6db]">
            {items.length > 0 && <FreeShippingBar total={total} />}

            {partnerSuggestions[0] && (
              <SetUpsellCard
                set={partnerSuggestions[0].set}
                partner={partnerSuggestions[0].partner}
                onClose={() => setIsOpen(false)}
                onAdd={onAddPartner}
              />
            )}

            <CartTotals
              subtotal={subtotal}
              setSavings={setSavings}
              total={total}
              appliedSets={appliedSets}
            />

            <button
              type="button"
              disabled={items.length === 0 || loading}
              onClick={onCheckout}
              className="w-full bg-[#1a1814] text-[#f0e9e1] py-4 micro-label hover:bg-[#1a1814]/85 disabled:opacity-40"
              data-testid="cart-checkout-btn"
            >
              {loading ? "Redirecting…" : "Proceed to Checkout"}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full text-center mt-3 micro-label text-[#1a1814]/60 hover:text-[#1a1814]"
              data-testid="cart-continue-btn"
            >
              Continue Browsing
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
